import { v4 as uuidV4 } from 'uuid';
import { calculateBmi } from '@features/forms/DonorProfileForm/utils';
import {
  DonorBlood,
  DonorData,
  DonorDemographics,
  DonorParameters,
  HeartData,
  LiverData,
  LungData,
  OrganType,
} from '@/api/apis/case';
import { cmToMm, gramsToKg, kgToGrams, mmToCm } from '@/utils/helpers';
import { DonorProfileFormValues, OrganSpecificDonorProfileFormValues, SharedDonorProfileFormValues } from './types';
import { getOrganSpecificDonorProfileDefaultValues, getSharedDonorProfileDefaultValues } from './defaults';

/**
 * Donor data request payload shared by both OPO endpoints.
 * Structurally compatible with nop-access's `UpdateOpoCaseDonorDataRequestBody`
 * and nop-connect's `UpdateDonorDataRequestBody`.
 */
export type DonorDataPayload = {
  donorDemographics?: DonorDemographics;
  donorParameters?: DonorParameters;
  donorBlood?: DonorBlood;
  reasonForUnallocated?: string | null;
  liverData?: LiverData;
  heartData?: HeartData;
  lungData?: LungData;
};

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

const toStringOrNull = (value: number | null | undefined): string | null =>
  value !== null && value !== undefined ? String(value) : null;

const toNumberOrNull = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const hasValue = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

const kgStringToGrams = (kg: string | null | undefined): number | null => {
  const kgNum = toNumberOrNull(kg);
  return kgNum === null ? null : kgToGrams(kgNum, 4);
};

const cmStringToMm = (cm: string | null | undefined): number | null => {
  const cmNum = toNumberOrNull(cm);
  return cmNum === null ? null : cmToMm(cmNum, 3);
};

// ---------------------------------------------------------------------------
// Form values → API payload builders
// ---------------------------------------------------------------------------

const buildDemographics = (values: SharedDonorProfileFormValues): DonorDemographics | undefined => {
  const { donorAge, donorWeight, donorHeight, donorGender, causeOfDeath } = values;

  if (!hasValue(donorAge) && !hasValue(donorWeight) && !hasValue(donorHeight) && !donorGender && !causeOfDeath) {
    return undefined;
  }

  return {
    age: toNumberOrNull(donorAge),
    weight: kgStringToGrams(donorWeight),
    height: cmStringToMm(donorHeight),
    gender: donorGender ?? null,
    causeOfDeath: causeOfDeath ?? null,
  };
};

const buildParameters = (values: SharedDonorProfileFormValues): DonorParameters | undefined => {
  const { neuroStatus, vasopressors, vasopressorsDetails, sustainedHypotension, sustainedHypotensionDetails } = values;

  if (!hasValue(neuroStatus) && !hasValue(vasopressors) && !hasValue(sustainedHypotension)) {
    return undefined;
  }

  return {
    neuroStatus: neuroStatus ?? null,
    vasopressors: vasopressors ?? null,
    vasopressorsDetails: vasopressorsDetails ?? null,
    sustainedHypotension: sustainedHypotension ?? null,
    sustainedHypotensionDetails: sustainedHypotensionDetails ?? null,
  };
};

const buildBlood = (values: SharedDonorProfileFormValues): DonorBlood | undefined => {
  const { aboType, positiveSerologies } = values;

  if (!aboType && !positiveSerologies) return undefined;

  return {
    abo: aboType ?? null,
    positiveSerologies: positiveSerologies ?? null,
  };
};

const buildLiverData = (values: OrganSpecificDonorProfileFormValues, organType: OrganType): LiverData | undefined => {
  if (organType !== OrganType.Liver) return undefined;

  return {
    totalBilirubinRecentResult1: toNumberOrNull(values.tbiliResult1),
    totalBilirubinRecentResult2: toNumberOrNull(values.tbiliResult2),
    aspartateAminotransferaseRecentResult1: toNumberOrNull(values.sgotAstResult1),
    aspartateAminotransferaseRecentResult2: toNumberOrNull(values.sgotAstResult2),
    alanineAminotransferaseRecentResult1: toNumberOrNull(values.sgptAltResult1),
    alanineAminotransferaseRecentResult2: toNumberOrNull(values.sgptAltResult2),
    ctScan: values.ctScan ?? null,
    ctScanDetails: values.ctScanDetails ?? null,
    ctScanLink: values.ctScanLink?.[0]?.url ?? null,
    ctScanDocuments: values.ctScanDocuments ?? null,
    willLocalSurgeonPerformOrganProcurement: values.localSurgeonToProcure ?? null,
    localSurgeonToProcureDetails: values.localSurgeonToProcureDetails ?? null,
    sedation: values.sedation ?? null,
  };
};

const buildHeartData = (values: OrganSpecificDonorProfileFormValues, organType: OrganType): HeartData | undefined => {
  if (organType !== OrganType.Heart) return undefined;

  return {
    matchRunOcsCenterNames: values.ocsMatchRunCenters ?? null,
  };
};

const buildLungData = (values: OrganSpecificDonorProfileFormValues, organType: OrganType): LungData | undefined => {
  if (organType !== OrganType.Lungs) return undefined;

  return {
    matchRunOcsCenterNames: values.ocsMatchRunCenters ?? null,
    mostRecentPfRatio: toNumberOrNull(values.pfRatio),
    netFluidBalanceOverPast24Hours: toNumberOrNull(values.fluidBalance24hr),
    cultureSensitivityResults: values.cultureSensitivities ?? null,
    ctScan: values.ctScan ?? null,
    ctScanDetails: values.ctScanDetails ?? null,
    ctScanLink: values.ctScanLink?.[0]?.url ?? null,
    ctScanDocuments: values.ctScanDocuments ?? null,
    sedation: values.sedation ?? null,
  };
};

export const mapSharedDonorDataPayload = (values: SharedDonorProfileFormValues): DonorDataPayload => ({
  donorDemographics: buildDemographics(values),
  donorParameters: buildParameters(values),
  donorBlood: buildBlood(values),
  reasonForUnallocated: values.reasonForUnallocated ?? null,
});

/** Spread-safe: only the matching organ key is present. */
export const mapOrganDonorDataPayload = (
  values: OrganSpecificDonorProfileFormValues,
  organType: OrganType,
): DonorDataPayload => {
  const liverData = buildLiverData(values, organType);
  const heartData = buildHeartData(values, organType);
  const lungData = buildLungData(values, organType);

  return {
    ...(liverData && { liverData }),
    ...(heartData && { heartData }),
    ...(lungData && { lungData }),
  };
};

export const mapDonorDataPayload = (values: DonorProfileFormValues, organType: OrganType): DonorDataPayload => ({
  ...mapSharedDonorDataPayload(values),
  liverData: buildLiverData(values, organType),
  heartData: buildHeartData(values, organType),
  lungData: buildLungData(values, organType),
});

/**
 * BE doesn't clear `donorData` when an organ is removed, so the block is overwritten with nulls.
 * Assumes nulls clear rather than merge — not BE-confirmed.
 */
export const buildClearedOrganData = (organType: OrganType): DonorDataPayload =>
  // ctScanLink's default placeholder row would otherwise send an empty string rather than null.
  mapOrganDonorDataPayload(
    { ...getOrganSpecificDonorProfileDefaultValues(), ctScanLink: [], ctScanDocuments: [] },
    organType,
  );

// ---------------------------------------------------------------------------
// API donor data → Form values
// ---------------------------------------------------------------------------

export const mapSharedDonorDataToOpoForm = (donorData: DonorData | null | undefined): SharedDonorProfileFormValues => {
  const base = getSharedDonorProfileDefaultValues();

  if (!donorData) return base;

  const { donorDemographics, donorParameters, donorBlood } = donorData;

  const donorWeight = hasValue(donorDemographics?.weight) ? String(gramsToKg(donorDemographics.weight, 3)) : null;
  const donorHeight = hasValue(donorDemographics?.height) ? String(mmToCm(donorDemographics.height, 2)) : null;

  return {
    ...base,
    neuroStatus: donorParameters?.neuroStatus ?? null,
    vasopressors: donorParameters?.vasopressors ?? null,
    vasopressorsDetails: donorParameters?.vasopressorsDetails ?? null,
    sustainedHypotension: donorParameters?.sustainedHypotension ?? null,
    sustainedHypotensionDetails: donorParameters?.sustainedHypotensionDetails ?? null,
    aboType: donorBlood?.abo ?? null,
    positiveSerologies: donorBlood?.positiveSerologies ?? null,
    donorAge: toStringOrNull(donorDemographics?.age),
    donorWeight,
    donorHeight,
    donorBmi: calculateBmi(donorWeight, donorHeight),
    donorGender: donorDemographics?.gender ?? null,
    causeOfDeath: donorDemographics?.causeOfDeath ?? null,
    reasonForUnallocated: donorData.reasonForUnallocated ?? null,
  };
};

/**
 * Don't fall back across blocks: a split-family case carries all three, so a fallback would
 * show one organ's values on another's form and write them into the wrong block on save.
 */
export const mapOrganDonorDataToOpoForm = (
  donorData: DonorData | null | undefined,
  organType: OrganType,
  matchId?: string | null,
): OrganSpecificDonorProfileFormValues => {
  const base = getOrganSpecificDonorProfileDefaultValues();

  if (!donorData) return { ...base, matchId: matchId ?? null };

  const liverData = organType === OrganType.Liver ? donorData.liverData : null;
  const heartData = organType === OrganType.Heart ? donorData.heartData : null;
  const lungData = organType === OrganType.Lungs ? donorData.lungData : null;

  const ctScanUrl = liverData?.ctScanLink ?? lungData?.ctScanLink ?? null;

  return {
    ...base,
    matchId: matchId ?? null,
    ocsMatchRunCenters: heartData?.matchRunOcsCenterNames ?? lungData?.matchRunOcsCenterNames ?? null,
    tbiliResult1: toStringOrNull(liverData?.totalBilirubinRecentResult1),
    tbiliResult2: toStringOrNull(liverData?.totalBilirubinRecentResult2),
    sgotAstResult1: toStringOrNull(liverData?.aspartateAminotransferaseRecentResult1),
    sgotAstResult2: toStringOrNull(liverData?.aspartateAminotransferaseRecentResult2),
    sgptAltResult1: toStringOrNull(liverData?.alanineAminotransferaseRecentResult1),
    sgptAltResult2: toStringOrNull(liverData?.alanineAminotransferaseRecentResult2),
    localSurgeonToProcure: liverData?.willLocalSurgeonPerformOrganProcurement ?? null,
    localSurgeonToProcureDetails: liverData?.localSurgeonToProcureDetails ?? null,
    ctScan: liverData?.ctScan ?? lungData?.ctScan ?? null,
    ctScanDetails: liverData?.ctScanDetails ?? lungData?.ctScanDetails ?? null,
    ctScanLink: ctScanUrl ? [{ id: uuidV4(), url: ctScanUrl, description: '', isNew: false }] : base.ctScanLink,
    ctScanDocuments: liverData?.ctScanDocuments ?? lungData?.ctScanDocuments ?? base.ctScanDocuments,
    sedation: liverData?.sedation ?? lungData?.sedation ?? null,
    pfRatio: toStringOrNull(lungData?.mostRecentPfRatio),
    fluidBalance24hr: toStringOrNull(lungData?.netFluidBalanceOverPast24Hours),
    cultureSensitivities: lungData?.cultureSensitivityResults ?? null,
  };
};

export const mapDonorDataToOpoForm = (
  donorData: DonorData | null | undefined,
  organType: OrganType,
  matchId?: string | null,
): DonorProfileFormValues => ({
  ...mapSharedDonorDataToOpoForm(donorData),
  ...mapOrganDonorDataToOpoForm(donorData, organType, matchId),
});
